import { Stack } from '@mui/material';
import {
  CentralAssortmentActionToolbar,
  CentralAssortmentTable,
} from '@retail/my-assortment/components';
import { AssortmentItemMinimal } from '@retail/my-assortment/types';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { ErrorFallback } from '@retail/components';
import { MgItemContainer, MgItemDialogContainer } from '@retail/products/containers';
import { useSelectedSearchParamState } from '@shared/hooks';
import { useCallback, useMemo } from 'react';
import { useCentralAssortmentMemberViewItems } from './useCentralAssortmentMemberViewItems';

interface Props {
  itemsChecked: AssortmentItemMinimal[];
  setItemsChecked: (items: AssortmentItemMinimal[]) => void;
  hideActionToolbar?: boolean;
  toggleAddCheckedItemsModal?: () => void;
  addItemToAssortment?: (item: AssortmentItemMinimal) => void;
}

export function CentralAssortmentItemsMemberView({
  itemsChecked,
  setItemsChecked,
  hideActionToolbar,
  toggleAddCheckedItemsModal,
  addItemToAssortment,
}: Props) {
  const { appTenant } = useSelectedOrgUnit();
  const { userCanRead, userCanUpdate } = usePermissionsForCurrentUser();
  const userCanAddItemsToAssortment = userCanUpdate('ASSORTMENT_ITEMS');

  const { items, isFetching, isError, errorMessage, refetch } =
    useCentralAssortmentMemberViewItems();

  const checkedItemIds = useMemo(
    () => itemsChecked.map(({ mgSupplierItemId }) => mgSupplierItemId),
    [itemsChecked]
  );

  const handleCheckItems = useCallback(
    (itemIds: number[]) =>
      setItemsChecked(
        items
          .filter((item) => itemIds.includes(item.mgSupplierItemId))
          // Tags belong to the central assortment, not to the assortment the items are added to
          .map((item) => ({ ...item, assortmentTags: [] })) as AssortmentItemMinimal[]
      ),
    [items, setItemsChecked]
  );

  const [selectedItem, setSelectedItem, selectItemById] = useSelectedSearchParamState({
    options: items,
    searchParamKey: 'item',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });

  if (isError) {
    return (
      <Stack flexGrow={1} justifyContent="center" overflow="auto" py={4}>
        <ErrorFallback message={errorMessage} resetError={() => refetch()} />
      </Stack>
    );
  }

  return (
    <Stack flexGrow={1} overflow="hidden">
      <CentralAssortmentTable
        items={items}
        itemsChecked={checkedItemIds}
        loading={isFetching}
        setItemsChecked={handleCheckItems}
        addItem={userCanAddItemsToAssortment ? addItemToAssortment : undefined}
        viewItem={selectItemById}
        externalItemSource={appTenant.externalItemSource}
        showMainSupplierName={appTenant.hasMainSupplier}
        checkboxSelection={userCanAddItemsToAssortment}
        hideSubAssortments={!userCanRead('SUB_ASSORTMENTS')}
      />

      {!hideActionToolbar && !!checkedItemIds.length && (
        <CentralAssortmentActionToolbar
          totalItems={items.length}
          handleAddItems={toggleAddCheckedItemsModal}
          checkedItems={checkedItemIds.length}
          resetCheckedItems={() => setItemsChecked([])}
        />
      )}

      {selectedItem && (
        <MgItemDialogContainer
          open
          onClose={() => setSelectedItem(null)}
          itemNumber={selectedItem.mgItemNumber}
        >
          {({ mgItem }) => (
            <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
          )}
        </MgItemDialogContainer>
      )}
    </Stack>
  );
}
